import { useRef, useState } from 'react';
import {
    IconArrowBackUp,
    IconArrowForwardUp,
    IconDeviceFloppy,
    IconDownload,
    IconFileTypePdf,
    IconLoader2,
    IconMinus,
    IconPencil,
    IconPrinter,
    IconSearch,
    IconX,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import { useCompactLayout } from '../ui/prims';
import Hint from '../../../components/common/Hint';

/** A tab-row icon button: 36px square, lit only on hover. */
function BarIcon({ icon: Icon, label, onClick, disabled, active = false, danger = false }) {
    return (
        <Hint label={label}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                aria-pressed={active || undefined}
                className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-35 ${
                    active
                        ? 'bg-primary-50 text-primary-700'
                        : danger
                            ? 'text-[#6B635C] hover:bg-[#FDECE7] hover:text-[#D33A2C] dark:text-zinc-300 dark:hover:bg-red-950/40'
                            : 'text-[#6B635C] hover:bg-[#F4F1ED] hover:text-[#1C1815] dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100'
                }`}
            >
                <Icon size={21} stroke={1.8} />
            </button>
        </Hint>
    );
}

/** Splits the actions into groups; dropped on a phone, where the row has no width to spare. */
function Divider() {
    const compact = useCompactLayout();
    if (compact) return null;
    return <span className="mx-1.5 h-6 w-px flex-none bg-[#ECE8E4] dark:bg-zinc-700" />;
}

/**
 * What the document is: name (click to rename), whether it is saved, how many
 * pages. Sits at the left end of the ribbon's tab row — the editor has no
 * separate title bar, so the tabs and the document share one line.
 */
export function DocTitle({ title, onTitleChange }) {
    const { t } = useTranslation();
    const dirty = usePdfEditorStore((s) => s.dirty);
    const pageCount = usePdfEditorStore((s) => s.pages.length);
    const [editingTitle, setEditingTitle] = useState(false);
    const inputRef = useRef(null);
    const compact = useCompactLayout();

    return (
        <div className={`flex min-w-0 items-center gap-1.5 ${compact ? 'max-w-[140px]' : 'max-w-[480px]'}`}>
            <IconFileTypePdf size={20} stroke={1.7} className="flex-none text-primary" />
            {editingTitle ? (
                <input
                    ref={inputRef}
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    onBlur={() => setEditingTitle(false)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Escape') setEditingTitle(false);
                        e.stopPropagation();
                    }}
                    className="h-8 w-full rounded-lg border border-primary bg-white px-2 text-[13px] font-semibold text-[#1C1815] outline-none dark:bg-zinc-900 dark:text-zinc-100"
                />
            ) : (
                <button
                    type="button"
                    onClick={() => { setEditingTitle(true); setTimeout(() => inputRef.current?.select(), 0); }}
                    className="flex min-w-0 items-center gap-1.5 rounded-lg px-1.5 py-1 transition-colors hover:bg-[#F4F1ED] dark:hover:bg-zinc-800"
                >
                    <span className="truncate text-[13px] font-semibold text-[#1C1815] dark:text-zinc-100">{title}</span>
                    <IconPencil size={13} stroke={1.8} className="flex-none text-[#A8A099]" />
                </button>
            )}
            {compact || editingTitle ? null : (
                <span className="flex-none whitespace-nowrap text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                    {dirty ? t('pdfEditor.unsaved') : t('pdfEditor.saved')}
                    {pageCount ? ` · ${t('pdfEditor.pageCount', { count: toBnDigits(pageCount) })}` : ''}
                </span>
            )}
        </div>
    );
}

/**
 * What you can do to the whole document, at the right end of the tab row:
 * search, undo/redo, print, download (save), then the window controls —
 * minimise and close.
 */
export function DocActions({
    onSearch,
    onPrint,
    onDownload,
    onSave,
    onMinimize,
    onClose,
    busy,
    // A long job's page count, so "working" does not look like "hung".
    progress,
    saving,
    // No document loaded yet: nothing to search, print or download.
    empty = false,
}) {
    const { t } = useTranslation();
    const dirty = usePdfEditorStore((s) => s.dirty);
    const showSearch = usePdfEditorStore((s) => s.showSearch);
    const undo = usePdfEditorStore((s) => s.undo);
    const redo = usePdfEditorStore((s) => s.redo);
    const canUndo = usePdfEditorStore((s) => s.past.length > 0);
    const canRedo = usePdfEditorStore((s) => s.future.length > 0);
    const compact = useCompactLayout();

    return (
        <div className="flex flex-none items-center gap-0.5">
            <BarIcon icon={IconSearch} label={t('pdfEditor.search')} onClick={onSearch} disabled={empty} active={showSearch} />

            <Divider />

            <BarIcon icon={IconArrowBackUp} label={t('pdfEditor.tools.undo')} onClick={undo} disabled={empty || !canUndo} />
            <BarIcon icon={IconArrowForwardUp} label={t('pdfEditor.tools.redo')} onClick={redo} disabled={empty || !canRedo} />

            <Divider />

            <BarIcon icon={IconPrinter} label={t('pdfEditor.print')} onClick={onPrint} disabled={busy || empty} />

            <Hint label={compact ? t('pdfEditor.download') : null}>
                <button
                    type="button"
                    onClick={onDownload}
                    disabled={busy || empty}
                    aria-label={t('pdfEditor.download')}
                    className="flex h-9 flex-none items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-semibold text-[#3D3833] transition-colors hover:bg-[#F4F1ED] hover:text-[#1C1815] disabled:pointer-events-none disabled:opacity-35 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                    {busy
                        ? <IconLoader2 size={21} className="animate-spin" />
                        : <IconDownload size={21} stroke={1.8} />}
                    {compact ? null : t('pdfEditor.download')}
                    {busy && progress ? (
                        <span className="tabular-nums opacity-80">
                            {progress.done}/{progress.total}
                        </span>
                    ) : null}
                </button>
            </Hint>

            {onSave ? (
                <button
                    type="button"
                    onClick={onSave}
                    disabled={saving || !dirty}
                    className="flex h-9 flex-none items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-semibold text-[#3D3833] transition-colors hover:bg-[#F4F1ED] hover:text-[#1C1815] disabled:opacity-40 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                    {saving
                        ? <IconLoader2 size={21} className="animate-spin" />
                        : <IconDeviceFloppy size={21} stroke={1.8} />}
                    {compact ? null : t('pdfEditor.save')}
                </button>
            ) : null}

            <Divider />

            {onMinimize ? (
                <BarIcon icon={IconMinus} label={t('pdfEditor.minimize')} onClick={onMinimize} />
            ) : null}
            <BarIcon icon={IconX} label={t('common.close')} onClick={onClose} danger />
        </div>
    );
}
